import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  CreditCard,
  DownloadSimple,
  MagnifyingGlass,
  Trash,
} from "phosphor-react";
import { Form, Modal } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import Loader from "../CustomCommons/Loader";
import { showToast } from "../../utils/ToastHelper";
import GcPagination from "./GcPagination";
import BulkActivateModal from "./BulkActivateModal";
import {
  ACTIVATABLE,
  GC_API,
  GC_STATUS,
  StatusPill,
  downloadXlsx,
  isSuperAdmin,
  money,
  showApiError,
} from "./giftCardCommon";
import "./css/giftcard.css";

const LIMIT = 20;
const COLUMNS = 9;

const GiftCardList = () => {
  const history = useHistory();
  const location = useLocation();
  const canDelete = isSuperAdmin();
  // The batch list links here with ?sku=<sku>, so that SKU starts selected.
  const initialSku = new URLSearchParams(location.search).get("sku") || "";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [skuOptions, setSkuOptions] = useState(initialSku ? [initialSku] : []);
  const [filters, setFilters] = useState({
    status: "",
    sku: initialSku,
    search: "",
  });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkActivate, setShowBulkActivate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Empty filters are left out entirely - an empty `sku=` would filter on a blank SKU.
  // The export endpoint takes the same query, so "Export" means "export what I am looking at".
  const queryString = Object.entries(filters)
    .filter(([, value]) => value !== "")
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");

  const loadUrl = (url) => {
    setIsLoading(true);
    axios
      .get(url)
      .then((res) => {
        setTotal(res.data?.data?.count || 0);
        setItems(res.data?.data?.results || []);
        setNextUrl(res.data?.data?.next);
        setPrevUrl(res.data?.data?.previous);
        setSelectedIds(new Set());
      })
      .catch((err) => showApiError(err, "Could not load gift cards"))
      .finally(() => setIsLoading(false));
  };

  const deleteCard = () => {
    setDeleting(true);
    axios
      .post(`${GC_API}admin/card/${deleteTarget?.id}/soft-delete/`)
      .then((res) => {
        const data = res.data?.data ?? res.data ?? {};
        showToast("success", data.detail || `Card #${deleteTarget?.id} deleted`);
        setDeleteTarget(null);
        loadUrl(`${GC_API}admin/card/?${queryString}`);
      })
      .catch((err) => {
        // Already gone (bad id or already hidden) - the row just disappears.
        if (err?.request?.status === 404) {
          showToast("success", "That card is already gone");
          setDeleteTarget(null);
          loadUrl(`${GC_API}admin/card/?${queryString}`);
          return;
        }
        showApiError(err, "Could not delete the card");
      })
      .finally(() => setDeleting(false));
  };

  useEffect(() => {
    setPageNumber(1);
    loadUrl(`${GC_API}admin/card/?${queryString}`);
  }, [queryString]);

  // Cards have no SKU endpoint of their own; batches are where SKUs are defined.
  useEffect(() => {
    axios
      .get(`${GC_API}admin/batch/`)
      .then((res) => {
        const skus = (res.data?.data?.results || [])
          .map((batch) => batch?.sku)
          .filter(Boolean);
        setSkuOptions((prev) => [...new Set([...prev, ...skus])].sort());
      })
      .catch(() => {});
  }, []);

  const onFilter = (e) =>
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const isFiltered = Object.values(filters).some((value) => value !== "");

  const selectableItems = items.filter((c) => ACTIVATABLE(c?.status));
  const allSelected =
    selectableItems.length > 0 &&
    selectableItems.every((c) => selectedIds.has(c.id));

  const toggleOne = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelectedIds(
      allSelected ? new Set() : new Set(selectableItems.map((c) => c.id))
    );

  return (
    <div className="categorey-parent gc-page">
      <div className="gc-head">
        <div>
          <h3>Gift Cards</h3>
          <p className="gc-sub">
            Look up any card, check its balance and open it for customer care.
          </p>
        </div>
        <div className="gc-actions">
          <button
            className="gc-btn gc-btn--ghost"
            onClick={() =>
              downloadXlsx(
                axios,
                `${GC_API}admin/card/export/?${queryString}`,
                "gift-cards.xlsx"
              )
            }
          >
            <DownloadSimple size={17} weight="bold" />
            Export
          </button>
        </div>
      </div>

      <div className="gc-panel">
        <div className="gc-panel__bar">
          <div className="gc-field gc-field--icon">
            <span className="gc-field__icon">
              <MagnifyingGlass size={17} />
            </span>
            <input
              className="gc-input"
              name="search"
              placeholder="Search card number"
              aria-label="Search card number"
              value={filters.search}
              onChange={onFilter}
            />
          </div>
          <div className="gc-field">
            <Form.Select
              className="gc-select"
              name="status"
              aria-label="Filter by status"
              value={filters.status}
              onChange={onFilter}
            >
              <option value="">All statuses</option>
              {Object.entries(GC_STATUS).map(([value, { label }]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="gc-field">
            <Form.Select
              className="gc-select"
              name="sku"
              aria-label="Filter by SKU"
              value={filters.sku}
              onChange={onFilter}
            >
              <option value="">All SKUs</option>
              {skuOptions.map((sku) => (
                <option key={sku} value={sku}>
                  {sku}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>

        {selectedIds.size > 0 && (
          <div className="gc-bulkbar">
            <span className="gc-bulkbar__count">
              {selectedIds.size} card{selectedIds.size === 1 ? "" : "s"}{" "}
              selected
            </span>
            <div className="gc-actions">
              <button
                className="gc-btn gc-btn--ghost gc-btn--sm"
                onClick={() => setSelectedIds(new Set())}
              >
                Clear
              </button>
              <button
                className="gc-btn gc-btn--sm"
                onClick={() => setShowBulkActivate(true)}
              >
                Activate Selected
              </button>
            </div>
          </div>
        )}

        <div className="gc-scroll">
          <table className="gc-table">
            <thead>
              <tr>
                <th style={{ width: "46px" }}>
                  <input
                    type="checkbox"
                    className="gc-checkbox"
                    aria-label="Select all activatable cards"
                    checked={allSelected}
                    disabled={selectableItems.length === 0}
                    onChange={toggleAll}
                  />
                </th>
                <th style={{ width: "80px" }}>ID</th>
                <th style={{ width: "210px" }}>Card Number</th>
                <th style={{ width: "130px" }}>SKU</th>
                <th style={{ width: "130px" }}>Value</th>
                <th style={{ width: "130px" }}>Balance</th>
                <th style={{ width: "140px" }}>Status</th>
                <th style={{ width: "150px" }}>Expiry</th>
                <th style={{ width: "190px", textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? null : items.length > 0 ? (
                items.map((card) => (
                  <tr
                    className="gc-row gc-row--clickable"
                    key={card?.id}
                    onClick={() =>
                      history.push(`/gift-card/card-detail/${card?.id}`)
                    }
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="gc-checkbox"
                        aria-label={`Select card ${card?.id}`}
                        checked={selectedIds.has(card?.id)}
                        disabled={!ACTIVATABLE(card?.status)}
                        title={
                          ACTIVATABLE(card?.status)
                            ? undefined
                            : `Already ${card?.status_display}`
                        }
                        onChange={() => toggleOne(card?.id)}
                      />
                    </td>
                    <td className="gc-num gc-strong">#{card?.id}</td>
                    <td className="gc-code">
                      {card?.formatted_number || card?.number}
                    </td>
                    <td className="gc-code">{card?.sku}</td>
                    <td className="gc-num">{money(card?.initial_amount)}</td>
                    <td className="gc-num gc-strong">{money(card?.balance)}</td>
                    <td>
                      <StatusPill
                        status={card?.status}
                        label={card?.status_display}
                      />
                    </td>
                    <td>{card?.expiry_date || "No expiry"}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="gc-cell-actions">
                        <button
                          className="gc-btn gc-btn--ghost gc-btn--sm"
                          onClick={() =>
                            history.push(`/gift-card/card-detail/${card?.id}`)
                          }
                        >
                          Details
                        </button>
                        {card?.status !== 2 && card?.status !== 4 && (
                          <button
                            className="gc-btn gc-btn--danger gc-btn--sm"
                            disabled={!canDelete}
                            title={
                              canDelete
                                ? undefined
                                : "Only Super Admin can delete a card"
                            }
                            onClick={() => setDeleteTarget(card)}
                          >
                            <Trash size={15} weight="bold" />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={COLUMNS}>
                    <div className="gc-empty">
                      <span className="gc-empty__icon">
                        <CreditCard size={24} />
                      </span>
                      <h4>{isFiltered ? "No matching cards" : "No cards yet"}</h4>
                      <p>
                        {isFiltered
                          ? "Try a different status or SKU, or clear the search."
                          : "Generate a batch from the Batches screen to create cards."}
                      </p>
                      {isFiltered && (
                        <button
                          className="gc-btn gc-btn--ghost gc-btn--sm"
                          onClick={() =>
                            setFilters({ status: "", sku: "", search: "" })
                          }
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {isLoading && (
          <div className="gc-loading">
            <Loader />
          </div>
        )}

        <GcPagination
          total={total}
          pageNumber={pageNumber}
          limit={LIMIT}
          prevUrl={prevUrl}
          nextUrl={nextUrl}
          isLoading={isLoading}
          onPrev={() => {
            loadUrl(prevUrl);
            setPageNumber((p) => Math.max(1, p - 1));
          }}
          onNext={() => {
            loadUrl(nextUrl);
            setPageNumber((p) => p + 1);
          }}
        />
      </div>

      <BulkActivateModal
        show={showBulkActivate}
        ids={[...selectedIds]}
        onHide={() => setShowBulkActivate(false)}
        onDone={() => loadUrl(`${GC_API}admin/card/?${queryString}`)}
      />

      <Modal
        centered
        dialogClassName="gc-modal"
        show={!!deleteTarget}
        onHide={() => !deleting && setDeleteTarget(null)}
      >
        <Modal.Header>
          <Modal.Title>Delete Card</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="gc-note">
            Are you sure you want to delete card{" "}
            {deleteTarget?.formatted_number || deleteTarget?.number}?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="gc-btn gc-btn--ghost"
            disabled={deleting}
            onClick={() => setDeleteTarget(null)}
          >
            Cancel
          </button>
          <button
            className="gc-btn gc-btn--danger"
            disabled={deleting}
            onClick={deleteCard}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GiftCardList;
